// «Nuevo chat de WhatsApp» (shared/API.md §15.4) — the bridge's `/#new` command as a form (port of
// ios/Sources/Views/WhatsAppChatSheet.swift; Android `WhatsAppChatSheet`). Country code, ten-digit
// number, contact name; on success the new conversation opens, joined, ready to write in. The wait
// is the bridge's (up to about a minute): the sheet says so, and closing it abandons only the
// waiting — the bridge finishes regardless.
import { useEffect, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import * as WhatsAppChatForm from '../../../core/whatsAppChatForm';
import { t, tf } from '../../../i18n/i18n';
import { Button, Dialog } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import '../people/people.css';
import './whatsAppChat.css';

/** The «+» menu shows the entry only on a WhatsApp line team (`/#new` AND `/#save`): cached, then re-measured. */
export function useWhatsAppLine(teamId: string | null): boolean {
  const [line, setLine] = useState(false);
  useEffect(() => {
    if (!teamId) return;
    let alive = true;
    void data.call('whatsAppLineCached', teamId).then((v) => alive && setLine(v), () => undefined);
    void data.call('refreshWhatsAppLine', teamId).then((v) => alive && setLine(v), () => undefined);
    return () => {
      alive = false;
    };
  }, [teamId]);
  return line;
}

export function WhatsAppChatDialog({ selectedChannelId, onClose }: { selectedChannelId: string | null; onClose(): void }) {
  const shell = useShell();
  const [countryCode, setCountryCode] = useState(WhatsAppChatForm.defaultCountryCode);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [working, setWorking] = useState(false);
  const [stillWorking, setStillWorking] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const token = useRef<string | null>(null);
  const submittable = WhatsAppChatForm.isSubmittable(countryCode, number, name);

  // Closing (Escape, the scrim, ✕) ends the wait — never the bridge's work.
  const close = () => {
    if (token.current) void data.call('cancelWhatsAppChat', token.current);
    token.current = null;
    onClose();
  };
  useEffect(
    () => () => {
      if (token.current) void data.call('cancelWhatsAppChat', token.current);
    },
    [],
  );

  const submit = async () => {
    if (!submittable || working) return;
    setWorking(true);
    setStillWorking(false);
    setFailure(null);
    const mine = crypto.randomUUID();
    token.current = mine;
    const outcome = await data
      .call('startWhatsAppChat', { token: mine, teamId: shell.team.id, selectedChannelId, countryCode, number, name })
      .catch(() => ({ kind: 'failed' as const, message: '' }));
    if (token.current !== mine) return; // closed meanwhile
    token.current = null;
    setWorking(false);
    switch (outcome.kind) {
      case 'opened':
        onClose();
        shell.openChannel(outcome.channelId);
        return;
      case 'noChannel':
        setFailure(t('whatsappChat.failedNoChannel'));
        return;
      case 'failed':
        setFailure(outcome.message || t('whatsappChat.failedOther'));
        return;
      case 'stillWorking':
        setStillWorking(true);
    }
  };

  const onEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <Dialog
      title={t('whatsappChat.title')}
      onClose={close}
      width={440}
      footer={
        <Button kind="primary" onClick={() => void submit()} disabled={!submittable || working}>
          {t('whatsappChat.create')}
        </Button>
      }
    >
      <div className="people-form">
        <div className="people-field">
          <span className="people-field-label">{t('whatsappChat.number')}</span>
          <div className="wa-number">
            <span className="wa-plus">+</span>
            <input
              className="people-input wa-cc"
              inputMode="numeric"
              aria-label={t('whatsappChat.countryCode')}
              placeholder={WhatsAppChatForm.defaultCountryCode}
              value={countryCode}
              onChange={(e) => {
                setCountryCode(WhatsAppChatForm.digits(e.target.value).slice(0, WhatsAppChatForm.countryCodeDigits));
                setFailure(null);
              }}
              onKeyDown={onEnter}
            />
            <input
              className="people-input"
              inputMode="numeric"
              autoFocus
              aria-label={t('whatsappChat.number')}
              placeholder={t('whatsappChat.numberPlaceholder')}
              value={number}
              // Pasted numbers arrive formatted ("55 1234-5678"); keep the digits.
              onChange={(e) => {
                setNumber(WhatsAppChatForm.digits(e.target.value).slice(0, WhatsAppChatForm.nationalDigits));
                setFailure(null);
              }}
              onKeyDown={onEnter}
            />
          </div>
          {submittable && !working ? <span className="people-field-hint">{tf('whatsappChat.numberPreview', WhatsAppChatForm.fullNumber(countryCode, number))}</span> : null}
        </div>
        <label className="people-field">
          <span className="people-field-label">{t('whatsappChat.name')}</span>
          <input
            className="people-input"
            placeholder={t('whatsappChat.namePlaceholder')}
            value={name}
            maxLength={WhatsAppChatForm.maxName}
            onChange={(e) => {
              setName(e.target.value);
              setFailure(null);
            }}
            onKeyDown={onEnter}
          />
        </label>
        {working || stillWorking ? (
          <div className="wa-status" role="status">
            {working ? <span className="wa-spinner" aria-hidden="true" /> : null}
            <span>{stillWorking ? t('whatsappChat.stillWorking') : t('whatsappChat.waiting')}</span>
          </div>
        ) : null}
        {failure ? (
          <div className="wa-failure" role="alert">
            {failure}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
